import { Input } from '@vkontakte/vkui';
import { useEffect, useRef, useState } from 'react';
import { t } from '../../i18n';
import { Chip, ChipRow, FilterBar, FilterSelect, type FilterOption } from '../../shared/ui';

/*
 * Личные метки предметов: фильтр над складом и редактор в карточке.
 *
 * Справочника нет — метки заводит сам игрок. Список собирает сервер по обеим
 * группам сразу (`GET /api/inventory/tags`), и он же фильтрует список: собрать
 * метки по загруженным предметам больше нельзя, склад постраничный.
 */

/** Зеркало ITEM_TAG_MAX_LENGTH и ITEM_TAGS_MAX: лишнего сервер не примет. */
const TAG_MAX_LENGTH = 40;
const TAGS_MAX = 20;

export interface TagFilterState {
  /** Все метки склада; пусто — фильтр показывать незачем. */
  tags: string[];
  /** null — «показать всё». */
  value: string | null;
}

/** «Все». Пустой метки не бывает: сервер режет пробелы и пустое выбрасывает. */
const ALL = '';

/** Фильтр — тот же чип-селектор, что у всех списков: вид у фильтров один. */
export function TagFilter({
  tags,
  value,
  onChange,
}: TagFilterState & {
  onChange: (tag: string | null) => void;
}) {
  if (tags.length === 0) return null;

  const options: FilterOption<string>[] = [
    { value: ALL, label: t('common.empty.all') },
    ...tags.map((tag) => ({ value: tag, label: tag })),
  ];

  return (
    <FilterBar>
      <FilterSelect
        label={t('inventory.tag.filter')}
        options={options}
        value={value ?? ALL}
        onChange={(next) => onChange(next === ALL ? null : next)}
      />
    </FilterBar>
  );
}

/**
 * Редактор меток одного предмета: правка мгновенная, проверку не проходит.
 *
 * Поле ввода прячется за пунктирной кнопкой «+ метка», как в макете. Пока оно
 * стояло открытым, у каждой карточки склада снизу висела пустая полоса во всю
 * ширину — на экране из пяти предметов это пять приглашений набрать текст,
 * которого от игрока никто не ждёт.
 */
export function TagEditor({
  tags,
  disabled,
  onSave,
}: {
  tags: readonly string[];
  disabled?: boolean;
  onSave: (tags: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  // Поле появляется по нажатию «добавить», и печатать человек хочет сразу.
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (adding) input.current?.focus();
  }, [adding]);

  const add = () => {
    const tag = draft.trim();
    setAdding(false);
    if (!tag) return;

    // Повтор не добавляем, но и не ругаемся: человек просто набрал то, что есть.
    const exists = tags.some((row) => row.toLocaleLowerCase('ru') === tag.toLocaleLowerCase('ru'));
    if (!exists) onSave([...tags, tag]);
    setDraft('');
  };

  if (adding) {
    return (
      <Input
        getRef={input}
        value={draft}
        maxLength={TAG_MAX_LENGTH}
        disabled={disabled}
        placeholder={t('inventory.tag.placeholder')}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setDraft('');
            setAdding(false);
            return;
          }
          if (event.key !== 'Enter') return;
          event.preventDefault();
          add();
        }}
        onBlur={add}
      />
    );
  }

  return (
    <ChipRow>
      {tags.map((tag) => (
        <Chip key={tag} onRemove={() => onSave(tags.filter((row) => row !== tag))}>
          {tag}
        </Chip>
      ))}
      {tags.length >= TAGS_MAX ? null : (
        <Chip
          add
          onClick={disabled ? undefined : () => setAdding(true)}
          label={t('inventory.tag.add.label')}
        >
          {t('inventory.tag.add.text')}
        </Chip>
      )}
    </ChipRow>
  );
}
